import React, { useContext, useState } from 'react';
import { ExperienceServiceV2 } from 'core/api/services/experiencesV2';
import { ExperienceResourceService } from 'core/api/services/ExperienceInfo/expResource';
import { ExperienceStatus } from '@utriper/core';
import { useDebounce } from '@utriper/core-ui';
import { toast } from 'react-toastify';
import { getExperienceData, parseExperience } from 'core/api/parser/experience';
import { useTranslation } from 'react-i18next';

const ExperiencesV2Context = React.createContext(null);

export const ExperiencesProviderV2 = ({ children }) => {
    const { t, i18n } = useTranslation();
    const translate = (key) => t(`experience_v2_context.${key}`);
    const [loading, setLoading] = useState(false);
    const [experiences, setExperiences] = useState([]);
    const [totalExperiences, setTotalExperiences] = useState(0);
    const [allExperiencesCount, setAllExperiencesCount] = useState(0);
    const [experienceCounts, setExperienceCounts] = useState({});
    const [updatingStatus, setUpdatingStatus] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [experience, setExperience] = useState(null);
    const [loadingRes, setLoadingRes] = useState(false);
    const [submittingRes, setSubmittingRes] = useState(false);
    const [resources, setResources] = useState([]);
    const [resourceCategories, setResourceCategories] = useState([]);
    // Experiences Filters
    const initialStatusFilters = [ExperienceStatus.IDS.live, ExperienceStatus.IDS.draft].map((status) => ({ id: status, name: ExperienceStatus.getTranslatedName(ExperienceStatus.status, status, i18n.language) }));
    const initialFilters = {
        status: initialStatusFilters,
        moduses: [],
        locationData: null,
        radius: 10,
    };
    const [expName, setExpName] = useState('');
    const [filters, setFilters] = useState({ ...initialFilters });
    const [expPage, setExpPage] = useState(1);
    const [expView, setExpView] = useState('gallery');
    const nameFilterDebounce = useDebounce(expName, 300);
    const [openFilters, setOpenFilters] = useState(false);
    const MAX_PAGE = 12;

    const resetPage = () => {
        if (expPage !== 1) {
            setExpPage(1);
        }
    };

    const handlePage = (event, newPage) => {
        setExpPage(newPage);
    };

    const handleFilters = (newFilters) => {
        setFilters({ ...newFilters });
        resetPage();
    };

    const handleSearch = (value) => {
        const trimmedValue = value?.trim();
        setExpName(trimmedValue);
        resetPage();
    };

    const handleToggleFilters = () => setOpenFilters(!openFilters);

    const handleView = (newView) => {
        if (newView) {
            setExpView(newView);
        }
    };

    const hasFilters = () => {
        const { status, moduses, locationData } = filters;

        if (expName?.length > 0) {
            return true;
        }

        if (status?.length > 0) {
            return true;
        }

        if (moduses?.length > 0) {
            return true;
        }

        if (locationData?.longitude || locationData?.latitude) {
            return true;
        }

        return false;
    };

    const resetFilters = (event) => {
        event?.preventDefault();

        setFilters({ ...initialFilters });
        setExpName('');
        resetPage();
    };

    const handleExperience = async (experienceId, history) => {
        if (history) {
            history?.push(`/experience/${experienceId}`);
        }
    };

    const getExperiences = async () => {
        try {
            setLoading(true);
            const { status, moduses, locationData, radius } = filters;
            const options = { name: nameFilterDebounce, status, moduses, longitude: locationData?.longitude, latitude: locationData?.latitude, radius, pager: { page: expPage, pageSize: MAX_PAGE } };

            const { experiences, total, allExperiencesCount, experienceCounts } = await ExperienceServiceV2.getList(options);

            if (!Array.isArray(experiences) || !Number.isInteger(total)) {
                throw new Error(translate('errors.get_experiences'));
            }

            setExperiences([...experiences]);
            setTotalExperiences(total);
            setAllExperiencesCount(allExperiencesCount);
            setExperienceCounts(experienceCounts);

            return true;
        } catch (error) {
            toast.error(error?.message, {
                position: 'top-right',
                theme: 'colored',
            });

            return false;
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async ({ experiences, status }) => {
        try {
            setUpdatingStatus(true);

            if (!Array.isArray(experiences) || experiences?.length <= 0) {
                throw new Error(translate('errors.update_status.no_experiences'));
            }

            if (!Number.isInteger(status)) {
                throw new Error(translate('errors.update_status.invalid_status'));
            }

            const result = await ExperienceServiceV2.updateStatus(experiences, status);

            if (!result) {
                throw new Error(translate('errors.update_status.error'));
            }

            if (experiences?.length > 0) {
                setExperiences((prevExperiences) => {
                    return [
                        ...prevExperiences.map((exp) => ({
                            ...exp,
                            status: experiences?.some((e) => e.id === exp.id) ? status : exp.status,
                        })),
                    ];
                });
            }

            if (Number.isInteger(experience?.id) && experiences?.some((exp) => exp.id === experience?.id)) {
                setExperience((prevExp) => ({
                    ...prevExp,
                    status,
                }));
            }

            toast.success(translate('success.update_status'), {
                position: 'top-right',
                theme: 'colored',
            });

            return result;
        } catch (error) {
            toast.error(error?.message, {
                position: 'top-right',
                theme: 'colored',
            });

            return false;
        } finally {
            setUpdatingStatus(false);
        }
    };

    const getExperienceById = async (experienceId, history) => {
        try {
            setLoading(true);

            if (!Number.isInteger(experienceId) || experienceId <= 0) {
                throw new Error(translate('errors.get_experience_by_id.invalid_id'));
            }

            if (Number.isInteger(experience?.id) && experience?.id === experienceId) return;

            const result = await ExperienceServiceV2.getById(experienceId);
            if (!result) {
                throw new Error(translate('errors.get_experience_by_id.error'));
            }

            const experienceParsed = parseExperience(result?.experience);

            setExperience({ ...experienceParsed });
            return true;
        } catch (error) {
            toast.error(error?.message, {
                position: 'top-right',
                theme: 'colored',
            });
            setExperience(null);

            if (history) {
                history?.push('/experience');
            }

            return false;
        } finally {
            setLoading(false);
        }
    };

    const loadResources = async (experienceId) => {
        try {
            setLoadingRes(true);

            // TODO: Use a single endpoint
            const { resources } = await ExperienceResourceService.getResourceExperience(experienceId);
            const { resourceCategories } = await ExperienceResourceService.getResourceCategoryExperience(experienceId);

            setResources([...resources]);
            setResourceCategories([...resourceCategories]);

            return true;
        } catch (error) {
            toast.error(error?.message, {
                position: 'top-right',
                theme: 'colored',
            });
            setResources([]);
            setResourceCategories([]);

            return false;
        } finally {
            setLoadingRes(false);
        }
    };

    const validateResourceid = ({ resourceId, resourceCategoryId, isCategory }) => {
        if (!Number.isInteger(resourceId) && !isCategory) {
            throw new Error(translate('errors.validate_resource_id.no_resource_id'));
        }

        if (!Number.isInteger(resourceCategoryId) && isCategory) {
            throw new Error(translate('errors.validate_resource_id.no_category_id'));
        }
    };

    const addResource = async ({ experienceId, resourceId, resourceCategoryId, isCategory = false }) => {
        try {
            setSubmittingRes(true);

            const resourceData = {
                resourceId,
                resourceCategoryId,
                notificationEmail: false,
                notificationWhatsapp: false,
            };

            validateResourceid({ resourceId, resourceCategoryId, isCategory });

            let result = false;

            if (!isCategory) {
                result = await ExperienceResourceService.addResource(experienceId, resourceData);
            } else {
                result = await ExperienceResourceService.addResourceCategory(experienceId, resourceData);
            }

            if (!result || result?.length === 0) {
                let error = translate('errors.add_resource.resource');

                if (isCategory) {
                    error = translate('errors.add_resource.category');
                }

                throw new Error(error);
            }

            let successMessage = translate('success.add_resource.resource');

            if (isCategory) {
                successMessage = translate('success.add_resource.category');
            }

            toast.success(successMessage, {
                position: 'top-right',
                theme: 'colored',
            });

            return true;
        } catch (error) {
            toast.error(error?.message, {
                position: 'top-right',
                theme: 'colored',
            });

            return false;
        } finally {
            setSubmittingRes(false);
            loadResources(experienceId);
        }
    };

    const updateResource = async ({ experienceId, id, notificationData, isCategory }) => {
        try {
            setLoadingRes(true);
            validateResourceid({ resourceId: id, resourceCategoryId: id, isCategory });
            const { notificationEmail, notificationWhatsapp } = notificationData;

            const updateData = {
                id,
                notificationEmail,
                notificationWhatsapp,
            };

            let result = false;

            if (isCategory) {
                result = await ExperienceResourceService.updateResourceCategory(experienceId, updateData);
            } else {
                result = await ExperienceResourceService.updateResource(experienceId, updateData);
            }

            if (!result || result?.length === 0) {
                let error = translate('errors.update_resource.resource');

                if (isCategory) {
                    error = translate('errors.update_resource.category');
                }

                throw new Error(error);
            }

            return true;
        } catch (error) {
            toast.error(error?.message, {
                position: 'top-right',
                theme: 'colored',
            });

            return false;
        } finally {
            setLoadingRes(false);
            loadResources(experienceId);
        }
    };

    const removeResource = async (experienceId, resourceId) => {
        try {
            setLoadingRes(true);

            const result = await ExperienceResourceService.deleteResource(experienceId, resourceId);

            if (!result?.[0]) {
                throw new Error(translate('errors.remove_resource'));
            }

            toast.success(translate('success.remove_resource'), {
                position: 'top-right',
                theme: 'colored',
            });

            return true;
        } catch (error) {
            toast.error(error?.message, {
                position: 'top-right',
                theme: 'colored',
            });

            return false;
        } finally {
            setLoadingRes(false);
            loadResources(experienceId);
        }
    };

    const removeResourceCategory = async (experienceId, resourceCategoryId) => {
        try {
            setLoadingRes(true);

            const result = await ExperienceResourceService.deleteResourceCategory(experienceId, resourceCategoryId);

            if (!result?.[0]) {
                throw new Error(translate('errors.remove_resource_category'));
            }

            toast.success(translate('success.remove_resource_category'), {
                position: 'top-right',
                theme: 'colored',
            });

            return true;
        } catch (error) {
            toast.error(error?.message, {
                position: 'top-right',
                theme: 'colored',
            });

            return false;
        } finally {
            setLoadingRes(false);
            loadResources(experienceId);
        }
    };

    const update = async (experienceId, values) => {
        try {
            setUpdating(true);

            const experienceData = getExperienceData(values, false);

            const result = await ExperienceServiceV2.update(experienceId, experienceData);

            if (!Number.isInteger(result?.id) || result?.id <= 0) {
                throw new Error(translate('errors.update'));
            }

            if (Number.isInteger(experience?.id) && experience?.id === result.id) {
                const experienceParsed = parseExperience(result);
                setExperience({ ...experienceParsed });
            }

            return result;
        } catch (error) {
            toast.error(error?.message, {
                position: 'top-right',
                theme: 'colored',
            });

            return false;
        } finally {
            setUpdating(false);
        }
    };

    const putExperienceMedia = async (experienceId, media = []) => {
        try {
            if (!Number.isInteger(experienceId) || experienceId <= 0) {
                throw new Error(translate('errors.put_experience_media.invalid_id'));
            }

            const mediaOrdered = media?.map((img, index) => ({ ...img, displayOrder: index })) || [];

            setIsUploading(true);

            const result = await ExperienceServiceV2.putMedia(experienceId, mediaOrdered);

            if (!result) {
                throw new Error(translate('errors.put_experience_media.error'));
            }

            if (Number.isInteger(experience?.id) && experience?.id === experienceId) {
                setExperience((prevExperience) => ({
                    ...prevExperience,
                    media: result,
                }));
            }

            return result;
        } catch (error) {
            toast.error(error?.message, {
                position: 'top-right',
                theme: 'colored',
            });

            return false;
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <ExperiencesV2Context.Provider
            value={{
                // Fetch Functions
                getExperiences,
                updateStatus,
                getExperienceById,
                update,
                putExperienceMedia,

                // Experiences List
                loading,
                experiences,
                totalExperiences,
                allExperiencesCount,
                experienceCounts,
                updatingStatus,
                handleExperience,

                // Experience ID
                experience,

                // Edit Experience
                updating,

                // Experience Media
                isUploading,

                // Experience Resources
                loadingRes,
                submittingRes,
                loadResources,
                addResource,
                resources,
                resourceCategories,
                removeResource,
                removeResourceCategory,
                updateResource,

                // Experiences Filters
                handlePage,
                expName,
                nameFilterDebounce,
                filters,
                setFilters,
                handleFilters,
                handleSearch,
                expPage,
                MAX_PAGE,
                hasFilters,
                resetFilters,
                expView,
                handleView,
                openFilters,
                handleToggleFilters,
            }}
        >
            {children}
        </ExperiencesV2Context.Provider>
    );
};

ExperiencesProviderV2.displayName = 'ExperiencesProviderV2';

export const useExperiences = () => useContext(ExperiencesV2Context);
